import { useLingui } from '@lingui/react/macro';
import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

import { Button } from '../Button';
import { CloseButton } from '../CloseButton';
import {
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '../Dialog';

export type ConfirmDialogContentProps = {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  colorPalette?: string;
  pending?: boolean;
  disabled?: boolean;
  cancelRef?: RefObject<HTMLButtonElement | null>;
  cancelAttributes?: Record<`data-${string}`, string | boolean | undefined>;
  confirmAttributes?: Record<`data-${string}`, string | boolean | undefined>;
  onCancel: () => void;
  onConfirm: () => void;
};

/** The same confirmation step inside an existing dialog or a standalone one. */
export const ConfirmDialogContent = ({
  title,
  description,
  children,
  icon,
  confirmLabel,
  cancelLabel,
  colorPalette = 'red',
  pending = false,
  disabled = false,
  cancelRef,
  cancelAttributes,
  confirmAttributes,
  onCancel,
  onConfirm,
}: ConfirmDialogContentProps) => {
  const { t } = useLingui();
  const localCancelRef = useRef<HTMLButtonElement>(null);
  const safeFocusRef = cancelRef ?? localCancelRef;

  // A next step has no new DialogRoot to move focus. Keep Enter on the safe action.
  useEffect(() => {
    // The root activates its focus trap in the next frame. Focus after that activation,
    // including a confirmation rendered as the dialog's initial step.
    let focusFrame: number | undefined;
    const frame = requestAnimationFrame(() => {
      focusFrame = requestAnimationFrame(() => safeFocusRef.current?.focus());
    });
    return () => {
      cancelAnimationFrame(frame);
      if (focusFrame !== undefined) cancelAnimationFrame(focusFrame);
    };
  }, [safeFocusRef]);

  return (
    <>
      <DialogHeader title={title} icon={icon} closable={false}>
        <CloseButton
          {...cancelAttributes}
          position="absolute"
          top="50%"
          insetEnd={3}
          transform="translateY(-50%)"
          size="md"
          minW={11}
          minH={11}
          tabIndex={-1}
          aria-label={t`Close`}
          disabled={pending}
          onClick={() => !pending && onCancel()}
        />
      </DialogHeader>
      {description || children ? (
        <DialogBody
          display="flex"
          flexDirection="column"
          gap={4}
          minH={0}
          overflowY="auto"
          color="fg.secondary"
          textStyle="body"
        >
          {description ? <DialogDescription>{description}</DialogDescription> : null}
          {children}
        </DialogBody>
      ) : null}
      <DialogFooter
        gap={2}
        flexDirection={{ base: 'column', md: 'row' }}
        flexWrap="wrap"
        alignItems="stretch"
        flexShrink={0}
      >
        <Button
          {...cancelAttributes}
          ref={safeFocusRef}
          variant="ghost"
          minH={11}
          whiteSpace="normal"
          h="auto"
          py={2}
          maxW="full"
          disabled={pending}
          onClick={() => !pending && onCancel()}
        >
          {cancelLabel ?? t`common:form.cancel`}
        </Button>
        <Button
          {...confirmAttributes}
          colorPalette={colorPalette}
          minH={11}
          whiteSpace="normal"
          h="auto"
          py={2}
          maxW="full"
          loading={pending}
          disabled={pending || disabled}
          onClick={() => !pending && !disabled && onConfirm()}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </>
  );
};

type ConfirmDialogProps = ConfirmDialogContentProps & {
  open: boolean;
  finalFocusEl?: () => HTMLElement | null;
};

/** Standalone confirmations use the product's adaptive dialog/sheet. */
export const ConfirmDialog = ({ open, finalFocusEl, ...props }: ConfirmDialogProps) => {
  const localCancelRef = useRef<HTMLButtonElement>(null);
  const cancelRef = props.cancelRef ?? localCancelRef;
  return (
    <DialogRoot
      open={open}
      role="dialog"
      initialFocusEl={() => cancelRef.current ?? undefined}
      finalFocusEl={finalFocusEl}
      closeOnEscape={!props.pending}
      closeOnInteractOutside={!props.pending}
      onOpenChange={({ open: nextOpen }) => {
        if (!nextOpen && !props.pending) props.onCancel();
      }}
    >
      <DialogContent maxW="md">
        <ConfirmDialogContent {...props} cancelRef={cancelRef} />
      </DialogContent>
    </DialogRoot>
  );
};
